import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Question } from 'survey-core';
import FileViewModal from './FileViewModal';
import { Checkbox } from 'antd';
import { Class2MedDevModel } from 'vl-md/src/CustomSurvey/customWidgets/Class2MedDevModel';

const formatQuestion = (question?: Question, fallback?: string) => {
  if (!question) return fallback;

  return question.title || question.name || question.html || fallback;
};

const PrintQuestion = ({ question, fallback }: { question?: Question; fallback: string }) => (
  <div dangerouslySetInnerHTML={{ __html: formatQuestion(question, fallback) }} />
);

const variableIsObject = (variable: unknown): boolean => {
  return typeof variable === 'object' && !Array.isArray(variable) && variable !== null;
};
const capitalizeFirstLetter = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatBoolean = (value: boolean | string | undefined, question?: Question): string | undefined => {
  // Some questions have descriptive text for true / false values
  // Use those if present (except if they are 0 or 1)
  // Otherwise convert to Yes / No.
  const isValueTruthy = (originalValue: boolean | string | undefined, question?: Question): boolean | undefined => {
    if (originalValue === undefined || originalValue === null) {
      return undefined;
    }

    if (question) {
      const { valueTrue, labelTrue, valueFalse, labelFalse } = question;
      if (valueTrue !== undefined && valueTrue !== null && originalValue === valueTrue) return true;
      if (labelTrue !== undefined && labelTrue !== null && originalValue === labelTrue) return true;
      if (valueFalse !== undefined && valueFalse !== null && originalValue === valueFalse) return false;
      if (labelFalse !== undefined && labelFalse !== null && originalValue === labelFalse) return false;
    }

    if (originalValue === '0' || originalValue === 'false') {
      return false;
    }

    return !!originalValue;
  };

  const booleanValue = isValueTruthy(value, question);

  if (booleanValue) {
    if (question?.valueTrue && question?.valueTrue !== '1') return question?.valueTrue;
    if (question?.labelTrue && question?.labelTrue !== '1') return question?.labelTrue;
    return 'Yes';
  }
  if (booleanValue === false) {
    if (question?.valueFalse && question?.valueFalse !== '0') return question?.valueFalse;
    if (question?.labelFalse && question?.labelFalse !== '0') return question?.labelFalse;
    return 'No';
  }
  return undefined;
};

const getValueDescriptionFromChoices = (value: string, question: Question): string => {
  // If the value from a radio/checkbox etc is either other or none
  // Check if question enables these and provides descriptive text for it
  if (value === 'other' && question.showOtherItem) {
    return question.otherText || value;
  } else if (value === 'none' && question.showNoneItem) {
    return question.noneText || value;
  }

  // Otherwise, loop through available choices on question to find text for value
  return (
    question?.choices?.find((c: Question['choices'][number]) => {
      if (typeof c !== 'string') {
        return c.value === value;
      }
    })?.text || value
  );
};

const getRatingLimitsStringFromQuestion = (question: Question): string => {
  // Find the max and min ratings for a rating question and format these into a string
  const hasMin = question?.rateMin !== undefined;
  const hasMax = question?.rateMax !== undefined;

  return (
    (hasMin && hasMax && ` (${question.rateMin}-${question.rateMax})`) ||
    (hasMax && ` (max value ${question.rateMax})`) ||
    ''
  );
};

export type FileWithoutContent = { type: string; name: string; url: string; crop?: any; label?: string };
export type FileWithContent = FileWithoutContent & { content: string };

type ValueObjectType = {
  [key: string]:
    | string
    | boolean
    | string[]
    | { [key: string]: string | boolean | string[] }
    | { [key: string]: string | boolean | string[] }[];
};
type ValueType = ValueObjectType | ValueObjectType[] | string | string[] | boolean;

const formatValue = (
  question: Question,
  value: ValueType
):
  | string
  | {
      type: 'a' | 'img';
      url: string;
      name: string;
      crop?: any;
      label?: any;
    }[]
  | string[]
  | (
      | {
          type: 'a' | 'img';
          url: string;
          name: string;
          crop?: any;
          label?: any;
        }
      | string
    )[] => {
  switch (question?.type) {
    case 'boolean':
      return formatBoolean(value as string | boolean, question) || '';

    case 'region-of-interest':
      /* Value is an array of objects with type
       * {
       *   content: string (s3 url)
       *   crop: { unit: string, x: number, y: number, width: number, height: number}
       *   name: string
       *   s3Filename: string (s3 url)
       *   type: string
       *   uploadedToS3: boolean
       * }
       */
      return (
        value as {
          content: string;
          crop: any;
          name: string;
        }[]
      )?.map?.(
        (file) =>
          ({
            type: 'a',
            url: file.content,
            name: file.name,
            crop: file.crop
          }) as const
      );

    case 'file':
      // Value is an array of objects with type { content: string, name: string }
      // Returns as an array of objects with type { type: 'a', url: string, name: string }
      return (
        value as {
          content: string;
          name: string;
        }[]
      )?.map?.(
        (file) =>
          ({
            type: 'a',
            url: file.content,
            name: file.name
          }) as const
      );

    case 'imagepicker':
      // imagepicker value is a string that references an item in the question.choices array
      // The item will have a descriptive text value
      return getValueDescriptionFromChoices(value as string, question);

    case 'checkbox':
      // checkbox value is an array of strings, each referencing an item in the question.choices array
      // Loop through each value to find the descriptive text
      return (value as string[])?.map?.((choice) => {
        return getValueDescriptionFromChoices(choice, question);
      });

    case 'matrixdropdown':
      // Complicated!!!
      // Matrix Dropdown questions are when the user can add multiple rows of the same thing
      // And the answers for each row are the same format
      // E.g previous test history (test name, when done, test outcome)
      // Or family history (relation, condition, symptoms)

      // matrixdropdown values are usually an object that contains key value pairs
      // The key is a string that references an item in the question.rows (subquestion) array
      // The value can be multiple types:
      // boolean, text, or a reference to an item in the question.choices array
      // Or the question.columns[i].choices array if there are multiple parts to the question

      // Question.rows is (usually) an array of objects - each object is a 'subquestion' with its own data identifying the question
      // Can sometimes (but rarely is) be a string.

      // Questions.columns is present when there are multiple values for a subquestion.
      // This represents the information for that particular 'column' e.g the choices available in the dropdown

      //                  Family relation?      What condition?    Relevant to yours?
      //                   col1 (reference)      col2 (string)      col3 (boolean)
      //  Entry 1   row1  item1 (-> Parent)       test string           false
      //  Entry 2   row2  item2 (-> Sibling)        asthma              true
      return (
        question.rows
          // Filter out values that are undefined
          ?.filter((subQuestion: { value: string; text: string } | string) => {
            if (typeof subQuestion === 'string') {
              return value !== undefined;
            } else {
              return (value as ValueObjectType)?.[subQuestion.value] !== undefined;
            }
          })
          .map((subQuestion: { value: string; text: string } | string) => {
            // If subquestion is not a string (most likely case)
            if (typeof subQuestion !== 'string') {
              // subquestion is an object, comprised of { value, text, ... }.
              // subquestion.value is the unique ID
              // The keys in the value objects will reference these unique IDs
              // So we can access the value object with this key to get the answer to this subquestion (will be an array)
              const subQuestionValue = Object.values((value as ValueObjectType)?.[subQuestion.value]);
              // Loop through each value for this subquestion and format individually
              const formattedSubQuestionValues = subQuestionValue.map((val: unknown, i: number) => {
                if (typeof val === 'boolean') {
                  // Converts to subquestion-supplied text or Yes / No
                  return formatBoolean(val || false, question?.columns[i]);
                } else if (question?.columns?.[i]?.choices) {
                  // Checks choices array for this particular subquestion value. Return text if provided
                  return getValueDescriptionFromChoices(val as string, question?.columns?.[i]);
                } else {
                  // If doesn't match anything else, just return val (most cases will be string)
                  return val;
                }
              });
              let formattedSubQuestionValue = '';
              if (formattedSubQuestionValues) {
                // Display array of answers as one string
                formattedSubQuestionValue = formattedSubQuestionValues.join(', '); //NOSONAR
              }
              // subQuestion.text will be the sub-question
              // formattedSubQuestionValue is all the provided answers associated with that sub-question
              return `${subQuestion.text}: ${formattedSubQuestionValue}`;
            } else if ((value as ValueObjectType)?.[subQuestion]) {
              // If row is a string then it sometimes points to a field in the value object
              // Get all the keys of the field in value and join with row
              // Sometimes it will be boolean, if so append this as 'Yes' or 'No'
              const subQuestionValuesKeys = [];
              for (const [key, subValue] of Object.entries((value as ValueObjectType)[subQuestion])) {
                if (typeof subValue === 'boolean') {
                  subQuestionValuesKeys.push(`${key}: ${subValue ? 'Yes' : 'No'}`);
                } else {
                  subQuestionValuesKeys.push(key);
                }
              }

              return `${subQuestion}: ${subQuestionValuesKeys.join(', ')}`;
            }
            // here subquestion is just a string & not an object
            // so access subquestion value by using it as the key
            return (value as ValueObjectType)?.[subQuestion]
              ? `${subQuestion}: ${(value as ValueObjectType)?.[subQuestion]}` //NOSONAR
              : undefined;
          }) || JSON.stringify(value, null, 2)
      );

    case 'matrixdynamic':
      // Complicated!!!
      // Matrix Dynamic questions are like matrix dropdowns but kind of with the axis flipped

      // matrixdynamic values are either undefined or an array of objects
      // We want to format each object in the array to be an array of strings with the format: {sub-question}: {answer}, {answer}
      // Type would be string[] for each object, so final type is string[][]
      // But we then flatten the array to string[] so it gets rendered as a list bullet points
      // Skip undefined answers
      if (value === undefined) return '';
      return Object.values(value as ValueObjectType)
        ?.map((subAnswer) => {
          // Loop through array of values
          // format row.value - can be text, boolean, dropdown
          return Object.keys(subAnswer as ValueObjectType)?.map((item) => {
            // keys in subAnswer all reference a different column.name
            // Find corresponding subquestion column using this to find subquestion info
            const subQuestionColumn = question.columns?.find((col: Question['choices'][number]) => col.name === item);
            // Find corresponding choice text to maybe use later
            const subQuestionColumnChoiceText = getValueDescriptionFromChoices(
              (subAnswer as ValueObjectType)[item] as string,
              subQuestionColumn
            );

            // Identify how to format answer text using standard formatting functions
            const answerText =
              typeof (subAnswer as ValueObjectType)[item] === 'boolean'
                ? formatBoolean(((subAnswer as ValueObjectType)[item] as string | boolean) || false, subQuestionColumn)
                : Array.isArray((subAnswer as ValueObjectType)[item]) //NOSONAR
                  ? ((subAnswer as ValueObjectType)[item] as string[])
                      ?.map((tickedBox: string) => getValueDescriptionFromChoices(tickedBox, subQuestionColumn))
                      .join(', ')
                  : subQuestionColumnChoiceText //NOSONAR Otherwise use choice text that we found earlier and use if it exists
                    ? subQuestionColumnChoiceText
                    : (subAnswer as ValueObjectType)[item] === 'other' && subQuestionColumn.showOtherItem //NOSONAR
                      ? subQuestionColumn.otherText // Otherwise if the choice text doesn't exist, it could be the other option on checkbox / radio questions
                      : (subAnswer as ValueObjectType)[item]; // Just return item value if doesn't match any other question (so likely just text answer)

            // Find rating limits and display if they exist
            const ratingLimits = getRatingLimitsStringFromQuestion(subQuestionColumn);
            // subQuestionColumn title is the most appropriate field, use it if it exists
            // some just use the name (which is the unique ID & always exists. item field usually refers to this)
            // if couldn't find subQuestionColumn, resort to using item itself - this sometimes is the question
            return `${subQuestionColumn?.title || subQuestionColumn?.name || item}: ${answerText}${ratingLimits}`;
          });
        })
        .flat();

    case 'paneldynamic':
      // complicated!!
      // We currently use this for our BMI question only - so might not be fully dynamic

      // value is array of objects - array length defined by question.panelCount
      // For each object in this array, the keys reference an entry in the question.templateElements array
      if (Array.isArray(value)) {
        const formattedAnswers = value
          .map((panelAnswers: ValueType) => {
            return Object.keys(panelAnswers)
              ?.map((panelAnswerKey: string) => {
                // Loop through value keys to find corresponding question elements
                // There could be multiple elements for a key e.g for BMI height, imperial format or metric format
                // We assume that if there's multiple elements for a single key, they will have different types
                // E.g string or number or object
                const correspondingQuestionElements = question.templateElements.filter(
                  (questionPart: Question['templateElements'][number]) =>
                    questionPart.valueName
                      ? questionPart.valueName === panelAnswerKey
                      : questionPart.name === panelAnswerKey
                );
                if (!correspondingQuestionElements?.length) return undefined;
                // Find the correct question elements to use based on number of relevant ones
                // If there is multiple, compare their types and the answer type
                const correctElement =
                  correspondingQuestionElements.length === 1
                    ? correspondingQuestionElements[0]
                    : correspondingQuestionElements.find((elem: Question['templateElements'][number]) => {
                        return (
                          (elem.type === 'multipletext' &&
                            variableIsObject((panelAnswers as ValueObjectType)[panelAnswerKey])) ||
                          (elem.type === 'text' &&
                            (typeof (panelAnswers as ValueObjectType)[panelAnswerKey] === 'number' ||
                              typeof (panelAnswers as ValueObjectType)[panelAnswerKey] === 'string'))
                        );
                      });

                // If no correct element, return undefined
                if (!correctElement) return undefined;

                // If element is expression and doesn't have title, hide
                if (correctElement.type === 'expression' && !correctElement.title) {
                  return undefined;
                }

                if (correctElement.type === 'region-of-interest') {
                  const answers: {
                    content: string;
                    crop: any;
                    name: string;
                  }[] = (panelAnswers as ValueObjectType)[panelAnswerKey] as {
                    content: string;
                    crop: any;
                    name: string;
                  }[];
                  const formatted = answers.map(
                    (file) =>
                      ({
                        type: 'a',
                        label: correctElement.name,
                        url: file.content,
                        name: file.name,
                        crop: file.crop
                      }) as const
                  );

                  return formatted;
                }

                // If element is multipletext, for each field in subanswer object, find the corresponding text label
                // (which is in the items array in an templateElements entry)
                if (correctElement.type === 'multipletext') {
                  const subValuesAsStrings = Object.keys((panelAnswers as ValueObjectType)[panelAnswerKey])?.map(
                    (panelAnswerSubKey) => {
                      const subKeyItem = correctElement.items?.find(
                        (item: Question['templateElements'][number]['items'][number]) => item.name === panelAnswerSubKey
                      );
                      if (!subKeyItem) return undefined;
                      return `${((panelAnswers as ValueObjectType)[panelAnswerKey] as ValueObjectType)[panelAnswerSubKey]} ${subKeyItem.title || subKeyItem.name}`; //NOSONAR
                    }
                  );
                  return `${correctElement.title || correctElement.name} ${subValuesAsStrings.join(', ')}`;
                }
                // If element is text, just find the units
                if (correctElement.type === 'text') {
                  return `${correctElement.title || correctElement.name} ${(panelAnswers as ValueObjectType)[panelAnswerKey]} ${correctElement.description || ''}`; //NOSONAR
                }
                // If element is an expression with a title, or isn't an expression
                // Standard formatting logic (for boolean/number/text for now)
                const subQuestionString =
                  correctElement?.title || (correctElement?.name && capitalizeFirstLetter(correctElement.name)) || null;

                let subValueString = (panelAnswers as ValueObjectType)[panelAnswerKey];
                if (typeof (panelAnswers as ValueObjectType)[panelAnswerKey] === 'boolean') {
                  subValueString = formatBoolean(
                    (panelAnswers as ValueObjectType)[panelAnswerKey] as string | boolean,
                    correctElement
                  )!;
                }
                return `${subQuestionString || ''}${subQuestionString ? ': ' : ''}${subValueString}`; //NOSONAR
              })
              .flat();
          })
          .flat()
          .filter((val) => val !== undefined);

        return formattedAnswers as string[]; //NOSONAR
      }

      return JSON.stringify(value, null, 2);

    case 'radiogroup':
      return getValueDescriptionFromChoices(value as string, question);

    case 'rating':
      // Display rating limits if question has them
      // Then loop through question rate values array to find value text
      return `${question.rateValues?.find((rate: Question['rateValues'][number]) => rate.value === value)?.text || value || ''}${getRatingLimitsStringFromQuestion(question)}`;

    case 'dropdown':
      // Loop through question choices array to find value text
      return getValueDescriptionFromChoices(value as string, question);

    case 'expression':
      return value as string;

    case 'comment':
      return value as string;

    default:
      return JSON.stringify(value, null, 2);
  }
};

const PrintValue = ({
  question,
  value,
  openFileModal,
  additionalValue
}: {
  question: Question;
  value: ValueType;
  openFileModal: any;
  additionalValue?: string;
}) => {
  const { t } = useTranslation('common');
  const formattedValue = formatValue(question, value);

  if (typeof formattedValue === 'string' || typeof formattedValue === 'number') {
    return (
      <span>
        {formattedValue}
        {additionalValue ? `, ${additionalValue}` : ''}
      </span>
    );
  }

  if (Array.isArray(formattedValue)) {
    return (
      <ul>
        {formattedValue.filter(Boolean).map((v) => {
          if (typeof v === 'string') {
            return <li key={v}>{v}</li>;
          }

          if (v.type === 'a') {
            return (
              <li key={v.name}>
                {
                  <span>
                    {v.label ? `${v.label}: ` : ''}
                    <a
                      role="button"
                      tabIndex={0}
                      onClick={() => openFileModal(v)}
                      onKeyDown={(ev) => ev.key === ' ' && openFileModal(v)}
                    >
                      {v.name}
                    </a>
                  </span>
                }
              </li>
            );
          }

          return <li key={v.name}>{v.name}</li>;
        })}
      </ul>
    );
  }

  if (formattedValue)
    return (
      <>
        {t('Unable to format value:')} {JSON.stringify(value)}
      </>
    );

  return <></>;
};

const Container = styled.div`
  position: relative;
  padding: 1em;
  width: 100%;

  .checkbox-container {
    position: absolute;
    top: -2.5em;
    right: 1em;
  }

  h1 {
    margin: 0;
  }

  table {
    width: 100%;
  }

  th {
    text-align: left;
    padding: 0.5em 1em;
    background-color: #fafafa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    h3 {
      margin-bottom: 0;
    }
  }

  td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    text-align: left;
    padding: 0.5em 1em;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  p {
    margin-bottom: 0.5em;
  }

  .question-row:nth-of-type(odd) {
    background-color: #fafafa;
  }

  .page-row {
    background-color: #bfbfbf;
    font-weight: bold;
    text-transform: uppercase;
  }

  .question-col {
    width: 40%;
  }
`;

const SurveyTableView = ({
  surveyModel,
  surveyRes,
  surveyDef,
  downloadFileFromS3
}: {
  surveyModel?: Class2MedDevModel;
  surveyRes: any;
  surveyDef: any;
  downloadFileFromS3: (val: FileWithoutContent) => Promise<FileWithContent>;
}) => {
  const { t } = useTranslation('common');
  const [hideUnansweredQuestions, setHideUnansweredQuestions] = useState(true);
  const [fileModalValue, setFileModalValue] = useState<FileWithoutContent>();
  const parsedResponse = Object?.entries(JSON.parse(surveyRes.response) || {});
  const surveyDefJson = JSON.parse(surveyDef.survey_json);
  const flattenedSurveyDef = {
    ...surveyDefJson,
    pages: undefined,
    questions: surveyDefJson.pages.flatMap((page: any) => {
      const pageEntry = {
        type: 'page',
        ...page
      };

      const pageQuestions = page.elements?.map((question: Question) => ({
        ...question
      }));

      return [pageEntry, ...pageQuestions];
    })
  };

  const hiddenSurveyQuestionsSet = useMemo(() => {
    return new Set(
      surveyModel
        ?.getAllQuestions()
        .filter((question) => !question.isVisible)
        .map((question) => question.name)
    );
  }, [surveyModel]);

  const getIsQuestionHiddenByCondition = (question: Question) => {
    return question.visibleIf && hiddenSurveyQuestionsSet.has(question?.name);
  };

  const getPageHasVisibleQuestions = (pageQuestions?: Question[]) => {
    if (!pageQuestions) {
      return false;
    }

    return pageQuestions.some((element: Question) => {
      if (!element || element.type === 'navigation' || !element.title || element.type === 'expression') {
        return false;
      }
      const questionAnswer = parsedResponse.find(([key]) => element.name === key);
      return questionAnswer !== undefined || !hideUnansweredQuestions;
    });
  };

  return (
    <Container>
      <div className="checkbox-container">
        <Checkbox
          className="hide-unanswered"
          checked={hideUnansweredQuestions}
          onChange={(e) => setHideUnansweredQuestions(e.target.checked)}
          data-testid="hide-unanswered-checkbox"
        >
          {t('Hide unanswered questions')}
        </Checkbox>
      </div>
      <table>
        <thead>
          <tr>
            <th className="question-col">
              <h3>{t('Question')}</h3>
            </th>
            <th>
              <h3>{t('Answer')}</h3>
            </th>
          </tr>
        </thead>

        <tbody>
          {flattenedSurveyDef.questions.map((question: Question) => {
            // If no question, it's skippable or is navigation
            if (!question || question?.type === 'navigation' || question?.type === 'youtube') {
              return null;
            }

            if (question.type === 'page') {
              const page = question;
              const pageQuestions = page.elements;

              const pageHasVisibleQuestions = getPageHasVisibleQuestions(pageQuestions);

              if (!pageHasVisibleQuestions) {
                return null;
              }

              return (
                <tr key={page.name} className="page-row">
                  <td colSpan={2}>{page.title || page.name}</td>
                </tr>
              );
            }

            // Search answers for corresponding answer to question
            const answer = parsedResponse.find(([key, value]) => {
              return question?.name === key;
            });

            // Some questions have a separate response for additional comments
            // Mostly for radioset / checkbox questions with an 'other' option
            // SurveyJS saves this in the response as [questionName]-Comment
            const additionalAnswer = parsedResponse.find(([key, value]) => {
              return `${question?.name}-Comment` === key;
            });

            // If there's no question title and no answer then the question is meant to be hidden
            if (
              (!question.title || question.type === 'expression' || hideUnansweredQuestions) &&
              answer === undefined
            ) {
              return null;
            }

            // If the question has a `visibleIf` condition and is currently hidden
            // according to that condition, it has to be excluded from table view.
            const isHiddenByConditionQuestion = getIsQuestionHiddenByCondition(question);
            if (isHiddenByConditionQuestion) {
              return null;
            }

            return (
              <tr key={question.name} className="question-row">
                <td className="question-col">
                  <PrintQuestion fallback={question.name} question={question} />
                </td>
                <td>
                  <PrintValue
                    question={question}
                    value={answer?.[1] as ValueType}
                    additionalValue={additionalAnswer?.[1] as string}
                    openFileModal={(val: FileWithoutContent) => {
                      setFileModalValue(val);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {fileModalValue && (
        <FileViewModal
          open
          value={fileModalValue}
          downloadFileFromS3={downloadFileFromS3}
          onClose={() => {
            setFileModalValue(undefined);
          }}
        />
      )}
    </Container>
  );
};

export default SurveyTableView;
